import { useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Modal, StatusPill, EmptyState, useToast } from '../../../design-system';
import GstinFields, { emptyGstin } from './GstinFields';
import GstPortalFetch from './GstPortalFetch';
import { stateLabel, stateCodeFromGstin } from '../../../lib/indiaStates';

const EMPTY = emptyGstin;
const dateOnly = (v) => (v ? v.slice(0, 10) : '');

// Add / edit / remove GSTINs, including all the extended metadata
// (registration type, QRMP frequency, status + cancellation, jurisdictions,
// ARN/provisional id) and the PPOB / APOB addresses. The field set itself is
// shared with the Add Client form via <GstinFields>.
//
// ⚠️ THE PORTAL LOOKUP IS NOT AN ONBOARDING-ONLY TOOL. It used to live only on
// the Add Client form, which had it backwards: a registration is read once when
// the client is created and then goes stale for years, and the things that
// change — a GSTIN moving to Cancelled or Suspended, a jurisdiction reassigned,
// an address updated — are exactly the ones the firm needs to know about. So it
// is here too, in two shapes:
//   • ADD    — pull a new registration the firm has just been given.
//   • REFRESH — re-read one already on file, with the number locked so the
//               fetch cannot land a different taxpayer on the row.
// Neither writes anything: the fetch fills the form, and the user still reviews
// and clicks Save. Overwriting a stored record straight from a captcha the user
// might have mistyped is not something to do behind their back.
function GstinManager({ clientId, gstins, onChange, client = null, onClientPatched = null }) {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The portal panel inside the modal, and whether the number is fixed.
  const [fetchOpen, setFetchOpen] = useState(false);
  const [lockedGstin, setLockedGstin] = useState('');
  // Client-level fields the portal can answer that this client is MISSING.
  // Offered, never applied silently — see applySuggested.
  const [suggested, setSuggested] = useState(null);

  const reset = (nextForm, { editingId = null, fetch = false, lock = '' } = {}) => {
    setEditing(editingId);
    setForm(nextForm);
    setError('');
    setSuggested(null);
    setFetchOpen(fetch);
    setLockedGstin(lock);
    setModalOpen(true);
  };

  const openAdd = () => reset(EMPTY());
  const openFetch = () => reset(EMPTY(), { fetch: true });

  const formFrom = (g) => ({
    ...EMPTY(),
    ...g,
    registrationDate: dateOnly(g.registrationDate),
    cancellationDate: dateOnly(g.cancellationDate),
    ppob: { ...EMPTY().ppob, ...(g.ppob || {}) },
    apob: (g.apob || []).map((a) => ({ ...a })),
  });

  const openEdit = (g) => reset(formFrom(g), { editingId: g._id });
  // Same modal as Edit, opened straight onto the portal panel with this row's
  // number locked in.
  const openRefresh = (g) => reset(formFrom(g), { editingId: g._id, fetch: true, lock: g.gstin });

  // The portal answered. Fill the form and let the user look at it.
  //
  // ⚠️ A REFRESH MERGES, IT DOES NOT REPLACE. `mapTaxpayerToGstin` returns
  // `apob: []`, `arn: ''` and `provisionalId: ''` — not because the taxpayer has
  // none, but because the record it reads does not carry them. Spreading that
  // over an existing row would silently delete every additional place of
  // business the firm had typed in, from a button labelled "Refresh". So the
  // portal owns only the fields it actually answers; everything else is the
  // firm's and survives.
  const FIRM_OWNED = ['filingFrequency', 'arn', 'provisionalId', 'apob', '_id'];

  const handleFetched = (fetched, portalSuggested) => {
    setForm((prev) => {
      const next = { ...EMPTY(), ...fetched };
      if (editing) for (const k of FIRM_OWNED) if (prev[k] !== undefined) next[k] = prev[k];
      return next;
    });
    setFetchOpen(false);

    // Only what is actually MISSING on the client. Offering to overwrite a name
    // somebody deliberately set is not help.
    if (portalSuggested && client) {
      const gaps = {};
      if (!client.legalName && portalSuggested.legalName) gaps.legalName = portalSuggested.legalName;
      if (!client.pan && portalSuggested.pan) gaps.pan = portalSuggested.pan;
      setSuggested(Object.keys(gaps).length ? gaps : null);
    }
    toast.success('Fetched from the GST portal — review and save');
  };

  const applySuggested = async () => {
    try {
      const res = await apiClient.put(`/api/clients/${clientId}`, suggested);
      onClientPatched?.(res.data);
      setSuggested(null);
      toast.success('Client details filled from the portal');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not update the client');
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.gstin.trim()) { setError('GSTIN is required'); return; }
    setSaving(true);
    setError('');
    // Drop empty date strings so we don't send "" where the API expects a Date.
    const payload = { ...form };
    if (!payload.registrationDate) delete payload.registrationDate;
    if (!payload.cancellationDate) delete payload.cancellationDate;
    try {
      const res = editing
        ? await apiClient.put(`/api/clients/${clientId}/gstins/${editing}`, payload)
        : await apiClient.post(`/api/clients/${clientId}/gstins`, payload);
      onChange(res.data);
      setModalOpen(false);
      toast.success(editing ? 'GSTIN updated' : 'GSTIN added');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save GSTIN');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (gstinId) => {
    if (!window.confirm('Remove this GSTIN?')) return;
    try {
      const res = await apiClient.delete(`/api/clients/${clientId}/gstins/${gstinId}`);
      onChange(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove GSTIN');
    }
  };

  return (
    <Card>
      <Card.Header
        title="GSTINs"
        action={
          <div className="gstin-actions">
            <Button variant="primary" size="sm" onClick={openFetch}>⇩ Fetch from GST Portal</Button>
            <Button variant="secondary" size="sm" onClick={openAdd}>+ Add manually</Button>
          </div>
        }
      />
      {gstins.length === 0 ? (
        <EmptyState
          icon="🧾"
          title="No GSTINs added"
          description="Fetch one from the GST portal to fill it in automatically, or add it by hand."
        />
      ) : (
        <div className="gstins-list">
          {gstins.map((g) => (
            <div key={g._id} className="gstin-card">
              <div className="gstin-info">
                <strong>{g.gstin}</strong>
                {/* Named, not a bare '27'. And read off the GSTIN when the
                    stored code is missing — rows saved before the code was
                    derived still carry the answer in the number itself. */}
                <span className="gstin-meta">
                  {stateLabel(g.stateCode) || stateLabel(stateCodeFromGstin(g.gstin)) || 'State: —'}
                </span>
                <span className="gstin-meta">{g.registrationType || 'Regular'} · {g.filingFrequency || 'Monthly'}</span>
                {g.registrationDate && <span className="gstin-meta">Reg: {dateOnly(g.registrationDate)}</span>}
                {g.arn && <span className="gstin-meta">ARN: {g.arn}</span>}
                <StatusPill status={g.status === 'Active' ? 'active' : 'inactive'} label={g.status} />
              </div>
              <div className="enrollment-actions">
                {/* The reason the lookup belongs here and not only at
                    onboarding: this is how a cancellation or a jurisdiction
                    change gets noticed. */}
                <Button variant="secondary" size="sm" onClick={() => openRefresh(g)} title="Re-read this registration from the GST portal">
                  ↻ Refresh
                </Button>
                <Button variant="secondary" size="sm" onClick={() => openEdit(g)}>Edit</Button>
                <button onClick={() => remove(g._id)} className="btn-remove">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? (lockedGstin ? `Refresh ${lockedGstin}` : 'Edit GSTIN') : 'Add GSTIN'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving}>{editing ? 'Save' : 'Add'}</Button>
          </>
        }
      >
        <form className="client-form" onSubmit={save}>
          {error && <div className="form-error" role="alert">{error}</div>}

          {fetchOpen ? (
            <GstPortalFetch
              onFetched={handleFetched}
              onCancel={() => setFetchOpen(false)}
              initialGstin={lockedGstin}
              lockGstin={!!lockedGstin}
            />
          ) : (
            !editing && (
              <p className="hint-text">
                <button type="button" className="gst-link" onClick={() => setFetchOpen(true)}>
                  Fetch from the GST portal
                </button>{' '}
                to fill these in automatically.
              </p>
            )
          )}

          {suggested && (
            <div className="gst-suggest">
              <span>
                The portal also has this client&apos;s{' '}
                {Object.keys(suggested).map((k) => (k === 'pan' ? 'PAN' : 'legal name')).join(' and ')}
                {suggested.legalName ? ` (${suggested.legalName})` : ''}. Fill it in?
              </span>
              <div className="gstin-actions">
                <Button type="button" variant="primary" size="sm" onClick={applySuggested}>Fill</Button>
                <Button type="button" variant="secondary" size="sm" onClick={() => setSuggested(null)}>No thanks</Button>
              </div>
            </div>
          )}

          <GstinFields value={form} onChange={setForm} />
        </form>
      </Modal>
    </Card>
  );
}

export default GstinManager;
